import { emit } from "../utils/dom";
import shared from "../data/js/shared";
import { useState, useEffect, useCallback, ComponentProps } from "react";
import Address from "./address";
import { useAppSession, useUI } from "../contexts";
import PanelDismiss from "./panel-dismiss";
import ShippingInput from "./shipping-input";
import { getCountries } from "./utils";

const {
  addressDrawerHeading,
  addressesHeading,
  addAddressHeading,
} = shared.checkout;

const {
  confirmRemovalHeading,
  confirmRemovalExplain,
  confirmRemovalLabel,
} = shared.account.addresses;

const AddressDrawer = ({
  shippingDrawerOpen,
  setShippingDrawerOpen,
  deactivateConfirm: externalDeactivateConfirm = null,
  setDefaultAddress: externalSetDefaultAddress = null,
}) => {
  const [countries, setCountries] = useState([]);
  const { setDefaultAddress: sessionSetDefaultAddress, getAddresses, addresses, deactivateAddress } = useAppSession();

  // Use external setDefaultAddress if provided, otherwise use the one from session
  const setDefaultAddress = externalSetDefaultAddress || sessionSetDefaultAddress;

  /**
   * Which address the radio shows as chosen, before the server agrees.
   *
   * `isDefault` arrives from the session, which only changes once the save has
   * returned AND the list has been re-fetched. That is long enough that the
   * radio sat on the old address while the drawer closed, and the tap read as
   * ignored. The choice is recorded here the moment it is made and the list is
   * drawn from it; the server's answer overwrites it when it lands.
   *
   * Cleared whenever the address list changes identity, so a refusal or a
   * change made elsewhere is not masked by a stale optimistic pick.
   */
  const [chosenId, setChosenId] = useState<string | number | null>(null);

  const chooseDefault = useCallback(
    async (id: string | number) => {
      setChosenId(id);
      try {
        return await setDefaultAddress(id);
      } finally {
        /**
         * The optimistic pick lasts exactly as long as the request.
         *
         * `setDefaultAddress` awaits its own re-fetch before it resolves
         * (contexts/AppSessionProvider.tsx), so by here `addresses` already
         * carries the new default and there is nothing to paper over. On a
         * refusal it carries the old one, which is the truth and what should be
         * shown -- holding the pick would leave the drawer lying about a save
         * that did not happen.
         */
        setChosenId(null);
      }
    },
    [setDefaultAddress],
  );
  const { setPopupOpen, setPopupContents } = useUI();

  /**
   * The list as the drawer should draw it: the optimistic choice wins over the
   * stored one, so the radio and the ordering move together on the tap.
   */
  const shownAddresses = addresses.map((a) =>
    chosenId === null ? a : { ...a, isDefault: String(a.id) === String(chosenId) },
  );

  // Sort addresses: default first, then by ID descending (newest first)
  const sortedAddresses = [...shownAddresses].sort((a, b) => {
    // Default addresses come first
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    // Then by ID descending (newest first). Every stored address has an id.
    return Number(b.id) - Number(a.id);
  });

  useEffect(() => {
    getCountries(setCountries);
  }, []);

  // Pre-fetch addresses when drawer opens
  useEffect(() => {
    if (shippingDrawerOpen) {
      void getAddresses();
    }
  }, [shippingDrawerOpen, getAddresses]);

  const confirmRemove = async (id: string) => {
    const success = await deactivateAddress(id);
    setPopupOpen(false);
    setPopupContents("");
    if (success) {
      emit('afterAddressDeleted');
    }
  };

  const deactivateConfirm = (address: Record<string, unknown> & { id: string }) => {
    setPopupContents(
      <div className="confirmRemoveAddress confirmPopup">
        <h3 className="heading">{confirmRemovalHeading}</h3>
        <p className="explain">{confirmRemovalExplain}</p>
        <div className="address-cont">
          <Address
            {...({ ...address, countries, radioButton: false } as unknown as ComponentProps<typeof Address>)}
          />
        </div>
        <button
          onClick={() => confirmRemove(address.id)}
          className="confirm-remove-button greenButton"
        >
          {confirmRemovalLabel}
        </button>
      </div>,
    );
    setPopupOpen(true);
  };

  // Use external function if provided, otherwise use internal one
  const handleDeactivate = externalDeactivateConfirm || deactivateConfirm;

  return (
    <>
      <div
        className="address-drawer-bg"
        onClick={() => {
          setShippingDrawerOpen(false);
        }}
      />
        <PanelDismiss
          side="Right"
          expanded={shippingDrawerOpen}
          className="address-drawer-cont"
          handle="address-drawer-handle"
          onChange={() => setShippingDrawerOpen(false)}
        >
          <div className="address-drawer">
            <div className="same-color inner">
              <h3 className="shipping-header same-color">
                {addressDrawerHeading}
              </h3>
              <h4 className="shipping-heading">{addAddressHeading}</h4>
              <ShippingInput
                // Managing addresses from the account, on no funnel of its own.
                funnel="seller_address"
                addresses={addresses as unknown as Record<string, unknown>[]}
                countries={countries}
                setShippingDrawerOpen={setShippingDrawerOpen}
              />

              {sortedAddresses.length > 0 &&
              <>
              <h4 className="shipping-heading">{addressesHeading}</h4>
              <div className="addressList">
                  {sortedAddresses.map((a, i) => {
                    return (
                      <Address
                        setDefaultAddress={chooseDefault}
                        index={i}
                        key={a.id}
                        {...a}
                        countries={countries}
                        setShippingDrawerOpen={setShippingDrawerOpen}
                        deactivateAddress={handleDeactivate}
                      />
                    );
                  })}
                </div>
                </>}
            </div>
          </div>
        </PanelDismiss>
    </>
  );
};
export default AddressDrawer;
